import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { buildSearchParams } from '../_helpers/utils';
import { APIResponse } from '../_types/api-responses.types';
import {
  AddCancellationDocumentRequest,
  CancelCancellationFlowRequest,
  CancellationFlowDetailsResponse,
  CancellationFlowPage,
  CancellationLetterPreviewRequest,
  CreateCancellationFlowRequest,
  CreateCancellationFlowResponse,
  EmailCancellationLetterRequest,
  ExportCancellationFlowsParams,
  GenerateCancellationLetterRequest,
  GetCancellationFlowsParams,
  ImportSignedCancellationLetterRequest,
  LinkBasePolicyRequest,
  LinkReplacementFlowRequest,
  RenderCancellationLetterMailResponse,
  SendCancellationToInsurerRequest,
  UpdateCancellationFlowRequest,
} from '../_types/cancellation.types';
import { UserAuthService } from './user-auth.service';

@Injectable({
  providedIn: 'root',
})
export class CancellationService {
  baseUrl: string;

  baseHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  constructor(
    private readonly httpClient: HttpClient,
    private readonly userAuthService: UserAuthService
  ) {
    this.baseUrl = `${environment.INSURANCEAPIURL}cancellation`;
  }

  get headers() {
    return this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
  }

  /**
   * Opens a cancellation, either from a policy we hold or for one held
   * elsewhere; the request's `origin` says which.
   */
  createCancellationFlow(
    request: CreateCancellationFlowRequest
  ): Observable<APIResponse<CreateCancellationFlowResponse>> {
    return this.httpClient.post<APIResponse<CreateCancellationFlowResponse>>(
      this.baseUrl,
      request,
      { headers: this.headers }
    );
  }

  getCancellationFlow(
    id: string
  ): Observable<APIResponse<CancellationFlowDetailsResponse>> {
    return this.httpClient.get<APIResponse<CancellationFlowDetailsResponse>>(
      `${this.baseUrl}/${id}`,
      { headers: this.headers }
    );
  }

  /**
   * The details of every cancellation of the flow's affaire — every flow
   * sharing its display id — whatever their status, newest first, each
   * timeline newest first as `getCancellationFlow` answers it. An affaire
   * without any is the normal case, answered with a success and `data: []`
   * — not a 404.
   */
  getCancellationFlowByInsuranceFlow(
    insuranceFlowId: string
  ): Observable<APIResponse<CancellationFlowDetailsResponse[]>> {
    return this.httpClient.get<APIResponse<CancellationFlowDetailsResponse[]>>(
      `${this.baseUrl}/by-insurance-flow/${insuranceFlowId}`,
      { headers: this.headers }
    );
  }

  getCancellationFlows(
    params: GetCancellationFlowsParams
  ): Observable<APIResponse<CancellationFlowPage>> {
    const searchParams = buildSearchParams(params);
    return this.httpClient.get<APIResponse<CancellationFlowPage>>(
      `${this.baseUrl}?${searchParams.toString()}`,
      { headers: this.headers }
    );
  }

  /**
   * The listing as a CSV file: same filters, every matching row, no paging.
   * Above the backend's row cap the request fails with a 400
   * (`CANCELLATION_EXPORT_TOO_LARGE`) instead of a truncated file.
   */
  exportCancellationFlows(
    params: ExportCancellationFlowsParams
  ): Observable<Blob> {
    const searchParams = buildSearchParams(params);
    return this.httpClient.get(
      `${this.baseUrl}/export?${searchParams.toString()}`,
      { headers: this.headers, responseType: 'blob' }
    );
  }

  updateCancellationFlow(
    id: string,
    request: UpdateCancellationFlowRequest
  ): Observable<APIResponse<CancellationFlowDetailsResponse>> {
    return this.httpClient.patch<APIResponse<CancellationFlowDetailsResponse>>(
      `${this.baseUrl}/${id}`,
      request,
      { headers: this.headers }
    );
  }

  /** Both links answer with the refreshed cancellation, so one response re-renders the page. */
  updateReplacement(
    id: string,
    request: LinkReplacementFlowRequest
  ): Observable<APIResponse<CancellationFlowDetailsResponse>> {
    return this.httpClient.patch<APIResponse<CancellationFlowDetailsResponse>>(
      `${this.baseUrl}/${id}/replacement`,
      request,
      { headers: this.headers }
    );
  }

  updateBasePolicy(
    id: string,
    request: LinkBasePolicyRequest
  ): Observable<APIResponse<CancellationFlowDetailsResponse>> {
    return this.httpClient.patch<APIResponse<CancellationFlowDetailsResponse>>(
      `${this.baseUrl}/${id}/base-policy`,
      request,
      { headers: this.headers }
    );
  }

  /** The letter as it would be filed, as a PDF; nothing is stored. */
  previewLetter(
    id: string,
    request: CancellationLetterPreviewRequest
  ): Observable<Blob> {
    return this.httpClient.post(
      `${this.baseUrl}/${id}/letter/preview`,
      request,
      {
        headers: this.headers,
        responseType: 'blob',
      }
    );
  }

  /** Every letter route answers with the refreshed cancellation. */
  generateLetterForPost(
    id: string,
    request: GenerateCancellationLetterRequest
  ): Observable<APIResponse<CancellationFlowDetailsResponse>> {
    return this.httpClient.post<APIResponse<CancellationFlowDetailsResponse>>(
      `${this.baseUrl}/${id}/letter/postal`,
      request,
      { headers: this.headers }
    );
  }

  renderLetterEmail(
    id: string,
    request: CancellationLetterPreviewRequest
  ): Observable<APIResponse<RenderCancellationLetterMailResponse>> {
    return this.httpClient.post<
      APIResponse<RenderCancellationLetterMailResponse>
    >(`${this.baseUrl}/${id}/letter/email/render`, request, {
      headers: this.headers,
    });
  }

  sendLetterByEmail(
    id: string,
    request: EmailCancellationLetterRequest
  ): Observable<APIResponse<CancellationFlowDetailsResponse>> {
    return this.httpClient.post<APIResponse<CancellationFlowDetailsResponse>>(
      `${this.baseUrl}/${id}/letter/email`,
      request,
      { headers: this.headers }
    );
  }

  /** The prefill of the mail carrying the signed letter to the insurer. */
  renderInsurerEmail(
    id: string
  ): Observable<APIResponse<RenderCancellationLetterMailResponse>> {
    return this.httpClient.get<
      APIResponse<RenderCancellationLetterMailResponse>
    >(`${this.baseUrl}/${id}/insurer/email/render`, { headers: this.headers });
  }

  /** Mails the signed letter to the insurer; the cancellation becomes SENT. */
  sendToInsurer(
    id: string,
    request: SendCancellationToInsurerRequest
  ): Observable<APIResponse<CancellationFlowDetailsResponse>> {
    return this.httpClient.post<APIResponse<CancellationFlowDetailsResponse>>(
      `${this.baseUrl}/${id}/insurer/email`,
      request,
      { headers: this.headers }
    );
  }

  /**
   * The insurer confirmed: the cancellation becomes CLOSED, and its policy, if
   * linked, is cancelled on the effective date (an ISO day, `yyyy-MM-dd`) —
   * right away when that day has come.
   */
  finalize(
    id: string,
    effectiveCancellationDate: string
  ): Observable<APIResponse<CancellationFlowDetailsResponse>> {
    return this.httpClient.post<APIResponse<CancellationFlowDetailsResponse>>(
      `${this.baseUrl}/${id}/finalize`,
      { effectiveCancellationDate },
      { headers: this.headers }
    );
  }

  /** Removes a cancellation opened by mistake; soft, and only while it is early. */
  deleteCancellationFlow(id: string): Observable<APIResponse<null>> {
    return this.httpClient.delete<APIResponse<null>>(`${this.baseUrl}/${id}`, {
      headers: this.headers,
    });
  }

  /** The request is called off: the cancellation becomes CANCELLED, with the reason it was. */
  cancel(
    id: string,
    request: CancelCancellationFlowRequest
  ): Observable<APIResponse<CancellationFlowDetailsResponse>> {
    return this.httpClient.post<APIResponse<CancellationFlowDetailsResponse>>(
      `${this.baseUrl}/${id}/cancel`,
      request,
      { headers: this.headers }
    );
  }

  /** The operator got the letter to the insurer themselves; the cancellation becomes SENT. */
  markSentToInsurer(
    id: string
  ): Observable<APIResponse<CancellationFlowDetailsResponse>> {
    return this.httpClient.post<APIResponse<CancellationFlowDetailsResponse>>(
      `${this.baseUrl}/${id}/insurer/mark-sent`,
      null,
      { headers: this.headers }
    );
  }

  importSignedLetter(
    id: string,
    request: ImportSignedCancellationLetterRequest
  ): Observable<APIResponse<CancellationFlowDetailsResponse>> {
    const form = new FormData();
    form.append('file', request.file, request.file.name);
    form.append('sendMethod', request.sendMethod);
    form.append('cancellationDate', request.cancellationDate);
    return this.httpClient.post<APIResponse<CancellationFlowDetailsResponse>>(
      `${this.baseUrl}/${id}/letter/import`,
      form,
      { headers: this.headers.delete('Content-Type') }
    );
  }

  /** Files a document on a running request; the letter date rides only for a letter. */
  addDocument(
    id: string,
    request: AddCancellationDocumentRequest
  ): Observable<APIResponse<CancellationFlowDetailsResponse>> {
    const form = new FormData();
    form.append('file', request.file, request.file.name);
    form.append('type', request.type);
    if (request.letterDate) {
      form.append('letterDate', request.letterDate);
    }
    return this.httpClient.post<APIResponse<CancellationFlowDetailsResponse>>(
      `${this.baseUrl}/${id}/documents`,
      form,
      { headers: this.headers.delete('Content-Type') }
    );
  }

  /** Takes a document off a running request; the file itself stays where it was stored. */
  deleteDocument(
    id: string,
    documentId: number
  ): Observable<APIResponse<CancellationFlowDetailsResponse>> {
    return this.httpClient.delete<APIResponse<CancellationFlowDetailsResponse>>(
      `${this.baseUrl}/${id}/documents/${documentId}`,
      { headers: this.headers }
    );
  }
}
